import React, { useState, useEffect, useCallback } from 'react'
import { useMedications } from '../../hooks/useMedications'
import { useI18n } from '../../hooks/useI18n'
import { Medication } from '../../types/medication'
import PillGenerator, { PillData } from './PillGenerator'
import PillEditorModal from './PillEditorModal'
import MedicationAutocomplete from '../ui/MedicationAutocomplete'
import { MedicationData } from '../../data/medicationsDatabase'
import ExceededLimitsModal from '../common/ExceededLimitsModal'

interface AddMedicationModalProps {
  isOpen: boolean
  onClose: () => void
  editingMedication?: Medication | null
}

const AddMedicationModal: React.FC<AddMedicationModalProps> = ({ isOpen, onClose, editingMedication }) => {
  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    instructions: '',
    dailyDose: '1',
    stock: '',
    maxStock: '',
    lowStockThreshold: '',
    times: [''],
    daysOfWeek: [] as number[],
    reminderEnabled: true,
    soundEnabled: true,
    notes: ''
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showLimitModal, setShowLimitModal] = useState(false)
  const [limitMessage, setLimitMessage] = useState('')
  const [showPillEditor, setShowPillEditor] = useState(false)
  const [pillData, setPillData] = useState<PillData>({
    shape: 'round',
    color: '#ffffff',
    pattern: 'none',
    shapeScale: 1,
    patternScale: 1,
    gradient: {
      enabled: false,
      colors: ['#ffffff', '#f0f0f0'],
      direction: 45
    },
    effects3D: {
      enabled: false,
      perspective: 1000,
      rotateX: 0,
      rotateY: 0,
      lighting: 50
    },
    svg: ''
  })

  const { addMedication, updateMedication } = useMedications()
  const { t } = useI18n()

  const isEditMode = !!editingMedication

  const daysOfWeekOptions = [
    { value: 0, label: t('sunday') },
    { value: 1, label: t('monday') },
    { value: 2, label: t('tuesday') },
    { value: 3, label: t('wednesday') },
    { value: 4, label: t('thursday') },
    { value: 5, label: t('friday') },
    { value: 6, label: t('saturday') }
  ]

  // Load medication data for editing
  useEffect(() => {
    if (isEditMode && editingMedication) {
      console.log('🔧 Loading medication for editing:', editingMedication)

      setFormData({
        name: editingMedication.name || '',
        dosage: editingMedication.dosage?.toString() || '',
        instructions: editingMedication.instructions || '',
        dailyDose: editingMedication.dailyDose?.toString() || editingMedication.frequency || '1',
        stock: editingMedication.currentStock?.toString() || editingMedication.stock?.toString() || '',
        maxStock: editingMedication.maxStock?.toString() || '30',
        lowStockThreshold: editingMedication.lowStockThreshold?.toString() || '',
        times: editingMedication.times && editingMedication.times.length > 0 ? editingMedication.times : ['08:00'],
        daysOfWeek: editingMedication.daysOfWeek && editingMedication.daysOfWeek.length > 0 ? editingMedication.daysOfWeek : [1, 2, 3, 4, 5, 6, 0],
        reminderEnabled: editingMedication.reminderEnabled ?? true,
        soundEnabled: editingMedication.soundEnabled ?? true,
        notes: editingMedication.notes || editingMedication.instructions || ''
      })

      console.log('📊 Form data set - daysOfWeek:', editingMedication.daysOfWeek)

      // Set pill data for editing
      setPillData({
        shape: editingMedication.pillShape || 'round',
        color: editingMedication.pillColor || '#ffffff',
        pattern: editingMedication.pillPattern || 'none',
        shapeScale: editingMedication.pillShapeScale || 1,
        patternScale: editingMedication.pillPatternScale || 1,
        gradient: editingMedication.pillGradient || {
          enabled: false,
          colors: ['#ffffff', '#f0f0f0'],
          direction: 45
        },
        patternThickness: editingMedication.pillPatternThickness || 1,
        svg: editingMedication.pillSvg || ''
      })

      console.log('💊 Pill data set:', editingMedication.pillShape, editingMedication.pillColor)
    } else {
      // Reset form for add mode
      console.log('➕ Resetting form for add mode')
      setFormData({
        name: '',
        dosage: '',
        instructions: '',
        dailyDose: '1',
        stock: '',
        maxStock: '',
        lowStockThreshold: '',
        times: [''],
        daysOfWeek: [],
        reminderEnabled: true,
        soundEnabled: true,
        notes: ''
      })

      // Reset pill data for add mode
      setPillData({
        shape: 'round',
        color: '#ffffff',
        pattern: 'none',
        shapeScale: 1,
        patternScale: 1,
        gradient: {
          enabled: false,
          colors: ['#ffffff', '#f0f0f0'],
          direction: 45
        },
        patternThickness: 1,
        svg: ''
      })
    }
  }, [isEditMode, editingMedication])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Validation
      if (!formData.name.trim()) {
        throw new Error(t('medication-name-required'))
      }
      if (!formData.dosage || parseFloat(formData.dosage) <= 0) {
        throw new Error(t('dose-greater-than-zero'))
      }
      if (formData.daysOfWeek.length === 0) {
        throw new Error(t('select-at-least-one-day'))
      }

      // Calculate low stock threshold if not provided
      const maxStock = parseInt(formData.maxStock) || 30
      const lowStockThreshold = formData.lowStockThreshold ?
        parseInt(formData.lowStockThreshold) :
        Math.ceil(maxStock * 0.2)

      const medicationData = {
        name: formData.name.trim(),
        dosage: parseFloat(formData.dosage),
        instructions: formData.instructions.trim(),
        dailyDose: parseInt(formData.dailyDose) || 1,
        stock: parseInt(formData.stock) || 0,
        currentStock: parseInt(formData.stock) || 0, // For consistency
        maxStock,
        lowStockThreshold,
        times: formData.times.filter(time => time.trim() !== ''),
        daysOfWeek: formData.daysOfWeek,
        reminderEnabled: formData.reminderEnabled,
        soundEnabled: formData.soundEnabled,
        notes: formData.notes.trim(),
        isActive: true,
        // Pill appearance data
        pillShape: pillData.shape,
        pillColor: pillData.color,
        pillPattern: pillData.pattern,
        pillShapeScale: pillData.shapeScale,
        pillPatternScale: pillData.patternScale,
        pillGradient: pillData.gradient,
        pillPatternThickness: pillData.patternThickness,
        pillSvg: pillData.svg
      }

      if (isEditMode && editingMedication) {
        await updateMedication(editingMedication.id, medicationData)
      } else {
        await addMedication(medicationData)
      }

      onClose()
    } catch (error) {
      console.error('Error adding medication:', error)
      const errorMessage = error instanceof Error ? error.message : t('error-adding-medication')

      if (errorMessage.includes('Plan limit reached')) {
        setLimitMessage(errorMessage)
        setShowLimitModal(true)
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const addTimeSlot = () => {
    setFormData(prev => ({
      ...prev,
      times: [...prev.times, '']
    }))
  }

  const removeTimeSlot = (index: number) => {
    setFormData(prev => ({
      ...prev,
      times: prev.times.filter((_, i) => i !== index)
    }))
  }

  const updateTime = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      times: prev.times.map((time, i) => i === index ? value : time)
    }))
  }

  const toggleDay = (day: number) => {
    setFormData(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day].sort()
    }))
  }

  const handlePillChange = useCallback((pillData: PillData) => {
    setPillData(pillData)
  }, [])

  const handleMedicationSelect = useCallback((medication: MedicationData) => {
    // Solo llenar el nombre, no otros campos automáticamente
  }, [])

  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Fixed Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditMode ? t('edit-medication') : t('add-medication')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('medication-name-required-label')}
                </label>
                <MedicationAutocomplete
                  value={formData.name}
                  onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
                  onMedicationSelect={handleMedicationSelect}
                  placeholder="Ej: Paracetamol"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('dose-mg-required')}
                </label>
                <input
                  type="number"
                  value={formData.dosage}
                  onChange={(e) => setFormData(prev => ({ ...prev, dosage: e.target.value }))}
                  className="input"
                  placeholder="500"
                  min="0"
                  step="0.1"
                  required
                />
              </div>
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('instructions')}
              </label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                className="input min-h-[80px] resize-y"
                placeholder={t('take-with-food-etc')}
                rows={3}
              />
            </div>

            {/* Stock Management */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('current-stock')}
                </label>
                <input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                  className="input"
                  placeholder="30"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('max-stock')}
                </label>
                <input
                  type="number"
                  value={formData.maxStock}
                  onChange={(e) => setFormData(prev => ({ ...prev, maxStock: e.target.value }))}
                  className="input"
                  placeholder="60"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('low-stock-alert')}
                </label>
                <input
                  type="number"
                  value={formData.lowStockThreshold}
                  onChange={(e) => setFormData(prev => ({ ...prev, lowStockThreshold: e.target.value }))}
                  className="input"
                  placeholder="10"
                  min="1"
                />
              </div>
            </div>

            {/* Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('taking-schedules')}
              </label>
              <div className="space-y-2">
                {formData.times.map((time, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => updateTime(index, e.target.value)}
                      className="input flex-1"
                    />
                    {formData.times.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTimeSlot(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addTimeSlot}
                  className="text-primary hover:text-primary/80 text-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {t('add-schedule')}
                </button>
              </div>
            </div>

            {/* Days of Week */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('days-of-week-required')}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {daysOfWeekOptions.map((day) => (
                  <label key={day.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.daysOfWeek.includes(day.value)}
                      onChange={() => toggleDay(day.value)}
                      className="rounded border-gray-300 text-primary focus:ring-primary mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {day.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.reminderEnabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, reminderEnabled: e.target.checked }))}
                  className="rounded border-gray-300 text-primary focus:ring-primary mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {t('enable-reminders')}
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.soundEnabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, soundEnabled: e.target.checked }))}
                  className="rounded border-gray-300 text-primary focus:ring-primary mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {t('sound-when-taking')}
                </span>
              </label>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('additional-notes')}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="input min-h-[60px] resize-y"
                placeholder={t('side-effects-reminders')}
                rows={2}
              />
            </div>

            {/* Pill Shape Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('medication-form')}
              </label>
              <div className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {/* Pill Preview */}
                <div className="flex items-center justify-center w-16 h-16 bg-white dark:bg-gray-600 rounded-lg shadow-sm">
                  <div dangerouslySetInnerHTML={{ __html: pillData.svg }} />
                </div>

                {/* Edit Button */}
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => setShowPillEditor(true)}
                    className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary dark:hover:border-primary transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('customize-form')}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('saving')}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {t('save')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Pill Editor Modal */}
      <PillEditorModal
        isOpen={showPillEditor}
        onClose={() => setShowPillEditor(false)}
        onPillChange={handlePillChange}
        initialPillData={pillData}
      />

      <ExceededLimitsModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        title="Límite de Medicamentos Alcanzado"
        message={limitMessage}
        limitType="medications"
      />
    </div>
  )
}

export default AddMedicationModal 